/**
 * Core domino types. `Tile = [low, high]` with the invariant `low <= high`
 * so two physically-identical tiles are referentially identical and
 * comparisons / set membership work via array equality on the canonical form.
 *
 * Pip values are 0..6 for the double-six set. The variant seam in `./variants`
 * makes the set size configurable for future variants (double-nine, etc.).
 */

export type Pip = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const ALL_PIPS: readonly Pip[] = [0, 1, 2, 3, 4, 5, 6];

export type Tile = readonly [Pip, Pip];

export type DominoPlayer = 'south' | 'west' | 'north' | 'east';

export const ALL_SEATS: readonly DominoPlayer[] = ['south', 'west', 'north', 'east'];

export function seatsFor(playerCount: 2 | 3 | 4): readonly DominoPlayer[] {
  switch (playerCount) {
    case 2:
      return ['south', 'north'];
    case 3:
      return ['south', 'west', 'north'];
    case 4:
      return ['south', 'west', 'north', 'east'];
  }
}

export function makeTile(a: number, b: number): Tile {
  if (!isPip(a) || !isPip(b)) {
    throw new Error(`makeTile: invalid pips ${a}, ${b}`);
  }
  return a <= b ? [a, b] : [b, a];
}

export function isPip(value: number): value is Pip {
  return Number.isInteger(value) && value >= 0 && value <= 6;
}

export function tilePips(tile: Tile): number {
  return tile[0] + tile[1];
}

export function isDouble(tile: Tile): boolean {
  return tile[0] === tile[1];
}

export function tileEquals(a: Tile, b: Tile): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

export function tileKey(tile: Tile): string {
  return `${tile[0]}-${tile[1]}`;
}

/**
 * Build the full double-six set: 28 tiles in canonical order, sorted by
 * heaviness (high pip then low pip ascending) so the lead-selection logic
 * has a deterministic "heaviest tile" without a separate sort.
 */
export function buildDoubleSixSet(): readonly Tile[] {
  const tiles: Tile[] = [];
  for (let a = 0; a <= 6; a += 1) {
    for (let b = a; b <= 6; b += 1) {
      tiles.push([a as Pip, b as Pip]);
    }
  }
  return tiles;
}

/**
 * The open end of one chain arm. `openValue` is the pip count visible at
 * the outward-facing position; for a non-double outer tile this is just
 * the non-matching half, for a double placed laterally this is 2 × face.
 * Renderers can derive "is the outer tile a double" from
 * `tiles[tiles.length - 1]` when they care about lateral orientation.
 */
export interface Arm {
  readonly tiles: readonly Tile[];
  readonly openValue: number;
}

/**
 * Chain state. The lead is the first tile played; the spinner is the first
 * double to be **covered** — not the first played, since a double sitting at
 * the end of an arm is still an open end and cannot be branched off. It may
 * be the lead (`lead === spinner`), but only once both main arms are built
 * on. See `Chain.ts`'s `findCoveredDouble` for the per-position rule.
 *
 * Arms count grows from 0 (empty) → 2 → 4, the last step happening when the
 * spinner is decided. Before that a double lead has exactly 2 arms and no
 * perpendicular ones at all, so a side play has no arm to land on.
 *
 * Active vs latent: a double lead's 2 main-axis arms share one Arm object so
 * its pip-sum is counted once while uncovered — the conventional All Fives
 * count of `2 × face` for the double opener (e.g. [5,5] lead scores 10). The
 * perpendicular arms are created latent: legal play targets immediately (a
 * spinner is covered by construction), but contributing 0 to the open-ends
 * sum until activated by their first tile.
 */
export interface Chain {
  readonly lead: Tile | null;
  readonly spinner: Tile | null;
  readonly arms: readonly Arm[];
  /** Indices of arms in `arms` that are active for scoring (subset of 0..arms.length-1). */
  readonly activeArms: readonly number[];
}

/**
 * Visual fold of a played tile relative to the previous one on its arm.
 * 'straight' continues in the arm's current direction; 'left'/'right' rotate
 * 90°; 'auto' lets the layout decide (used for AI plays — apply the
 * segment-length-based auto-fold rule). Stored in a parallel side-state
 * (not on Chain) since the engine doesn't need direction information for
 * pip-matching or scoring.
 */
export type Turn = 'straight' | 'left' | 'right' | 'auto';

export type DominoMode = 'hot-seat' | 'ai' | 'online';

/**
 * Most recent engine-mutating action in the current hand. Lives on
 * `GameStateData` so it survives `STATE_SYNC` (network) and replay state
 * reconstruction without extra side channels. The UI uses it to surface a
 * "P1 played [2,3], scored 10 — P2's turn" banner. Cleared on hand boundaries.
 */
export type LastAction =
  | { readonly kind: 'play'; readonly seat: number; readonly tile: Tile; readonly points: number; readonly wentOut: boolean }
  | { readonly kind: 'draw'; readonly seat: number }
  | { readonly kind: 'pass'; readonly seat: number }
  | {
      readonly kind: 'claim-muggins';
      readonly seat: number;
      readonly points: number;
      /** Tile from the triggering play, surfaced in the banner. Absent when unknown (legacy snapshots). */
      readonly tile?: Tile;
    };

/** Partnership mapping: each seat's index → team id (0 or 1). FFA = each seat its own team. */
export interface TeamMap {
  readonly teamOf: readonly number[];
  readonly teamCount: number;
}

export function teamMapFor(playerCount: 2 | 3 | 4, partnership: boolean): TeamMap {
  if (playerCount === 4 && partnership) {
    return { teamOf: [0, 1, 0, 1], teamCount: 2 };
  }
  // Free-for-all: each seat is its own team.
  return { teamOf: Array.from({ length: playerCount }, (_, i) => i), teamCount: playerCount };
}
